import React from 'react';
import {
  Stack,
  Typography,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { PageTitle } from 'components/shared/page_title';
import DocumentComponent from 'components/shared/document_component';
import { useLocation } from 'react-router-dom';


type NumericStringNull = number | string | null;

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
  fonte?: 'federal' | 'estadual' | 'municipal';
  tipoFornecedor?: 'CNPJ' | 'CPF';
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

interface ViewSchoolFeedingProps {
  values: {
    alimentacao_af_recurso_pnae: NumericStringNull;
    alimentacao_af_recurso_estado: NumericStringNull;
    alimentacao_af_recurso_prefeitura: NumericStringNull;
    alimentacao_total_recurso_pnae: NumericStringNull;
    alimentacao_total_recurso_estado: NumericStringNull;
    alimentacao_total_recurso_prefeitura: NumericStringNull;
    alimentacao_ata_aprovacao_uuid?: string | null;
    secoesAquisicao?: SecaoAquisicao[];
  };
}

export const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
  if (!valor && valor !== 0) return '';
  const numeroValor = typeof valor === 'string' ? parseFloat(valor.toString().replace(/\./g, '').replace(',', '.')) : valor;
  if (isNaN(numeroValor)) return '';
  return numeroValor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const FONTES = [
  { key: 'federal' as const, label: 'Federal (PNAE)' },
  { key: 'estadual' as const, label: 'Estadual' },
  { key: 'municipal' as const, label: 'Municipal' },
];

export const extractAnoFromPath = (path?: string) => {
  if (!path) return 2026;
  const match = /cifa-(\d{4})/.exec(path);
  return match ? Number(match[1]) : 2026;
};

export const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
  return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
};

const ViewSchoolFeeding: React.FC<ViewSchoolFeedingProps> = ({ values }) => {
  const location = useLocation();
  const anoReferencia = extractAnoFromPath(location.pathname);

  // Totais calculados automaticamente
  const totalAF =
    Number(values.alimentacao_af_recurso_pnae ?? 0) +
    Number(values.alimentacao_af_recurso_estado ?? 0) +
    Number(values.alimentacao_af_recurso_prefeitura ?? 0);

  const totalGeral =
    Number(values.alimentacao_total_recurso_pnae ?? 0) +
    Number(values.alimentacao_total_recurso_estado ?? 0) +
    Number(values.alimentacao_total_recurso_prefeitura ?? 0);

  return (
    <>
      <PageTitle
        title={`Valores para Aquisição de Alimentação Escolar - Em ${anoReferencia}`}
        subtitle={`Visualização dos valores declarados para aquisição de alimentação escolar em ${anoReferencia}.`}
      />

      <Stack marginX='4rem' marginTop='1rem' spacing='2rem' flexDirection='column' maxWidth='1152px' width='100%'>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Valores Declarados para Agricultura Familiar
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Recurso do PNAE Federal (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_pnae)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Recurso do Governo do Estado (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_estado)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Recurso da Prefeitura (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_prefeitura)}
              </Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Typography fontWeight={600}>
                Total de Recursos Aplicados nas Aquisições da Agricultura Familiar:{' '}
                <Typography component="span" color="primary" fontWeight={700}>
                  R$ {formatarValorParaExibicao(totalAF)}
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Valores Totais Declarados na Alimentação Escolar
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Total de Recursos PNAE Federal (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_pnae)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Total de Recursos do Governo do Estado (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_estado)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={500}>Total de Recursos da Prefeitura (R$)</Typography>
              <Typography>
                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_prefeitura)}
              </Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Typography fontWeight={600}>
                Total Geral de Recursos na Alimentação Escolar:{' '}
                <Typography component="span" color="primary" fontWeight={700}>
                  R$ {formatarValorParaExibicao(totalGeral)}
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Ata de Aprovação dos Dados de Alimentação Escolar
          </Typography>
          {values.alimentacao_ata_aprovacao_uuid ? (
            <Box>
              <DocumentComponent
                uuid={values.alimentacao_ata_aprovacao_uuid}
                podeEnviar={false}
                title="Ata de Aprovação - Alimentação Escolar"
              />
            </Box>
          ) : (
            <Typography color="text.secondary">
              Nenhuma ata de aprovação anexada.
            </Typography>
          )}
        </Paper>

        {/* Seção de Aquisições Detalhadas */}
        {values.secoesAquisicao && values.secoesAquisicao.length > 0 && (
          <>
            {values.secoesAquisicao.map((secao, secaoIndex) => (
              <Paper key={getSecaoKey(secao, secaoIndex)} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  {secao.titulo}
                </Typography>

                {/* Verifica se os produtos têm fonte definida */}
                {secao.produtosCnpj.some(p => p.fonte) || secao.produtosCpf.some(p => p.fonte) ? (
                  // Se tem fonte, agrupa por fonte
                  FONTES.map(fonte => {
                    const produtosCnpj = secao.produtosCnpj.filter(p => p.fonte === fonte.key);
                    const produtosCpf = secao.produtosCpf.filter(p => p.fonte === fonte.key);

                    if (produtosCnpj.length === 0 && produtosCpf.length === 0) return null;

                    return (
                      <Box key={fonte.key} mb={3}>
                        <Typography variant="subtitle1" fontWeight={500} mb={1}>
                          Fonte de recurso: {fonte.label}
                        </Typography>

                        {/* Fornecedores CNPJ */}
                        {produtosCnpj.length > 0 && (
                          <>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                              Fornecedor: CNPJ
                            </Typography>
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Nome/Razão Social</TableCell>
                                    <TableCell>CNPJ</TableCell>
                                    <TableCell>Valor (R$)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {produtosCnpj.map(produto => (
                                    <TableRow key={produto.id}>
                                      <TableCell>{produto.nomeRazao}</TableCell>
                                      <TableCell>{produto.documento}</TableCell>
                                      <TableCell>R$ {formatarValorParaExibicao(produto.valorNumerico)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}

                        {/* Fornecedores CPF */}
                        {produtosCpf.length > 0 && (
                          <>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                              Fornecedor: CPF
                            </Typography>
                            <TableContainer component={Paper}>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Nome/Razão Social</TableCell>
                                    <TableCell>CPF</TableCell>
                                    <TableCell>Valor (R$)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {produtosCpf.map(produto => (
                                    <TableRow key={produto.id}>
                                      <TableCell>{produto.nomeRazao}</TableCell>
                                      <TableCell>{produto.documento}</TableCell>
                                      <TableCell>R$ {formatarValorParaExibicao(produto.valorNumerico)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  // Se não tem fonte, exibe normalmente sem agrupar
                  <>
                    {/* Fornecedores CNPJ */}
                    {secao.produtosCnpj.length > 0 && (
                      <Box mb={3}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                          Fornecedor: CNPJ
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 2 }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Nome/Razão Social</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Valor (R$)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {secao.produtosCnpj.map(produto => (
                                <TableRow key={produto.id}>
                                  <TableCell>{produto.nomeRazao}</TableCell>
                                  <TableCell>{produto.documento}</TableCell>
                                  <TableCell>R$ {formatarValorParaExibicao(produto.valorNumerico)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* Fornecedores CPF */}
                    {secao.produtosCpf.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                          Fornecedor: CPF
                        </Typography>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Nome/Razão Social</TableCell>
                                <TableCell>CPF</TableCell>
                                <TableCell>Valor (R$)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {secao.produtosCpf.map(produto => (
                                <TableRow key={produto.id}>
                                  <TableCell>{produto.nomeRazao}</TableCell>
                                  <TableCell>{produto.documento}</TableCell>
                                  <TableCell>R$ {formatarValorParaExibicao(produto.valorNumerico)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            ))}
          </>
        )}
      </Stack>
    </>
  );
};

export default ViewSchoolFeeding;
